import { supabase } from "@/infrastructure/supabase/client";
import type { ProfileRow } from "@/infrastructure/supabase/database.types";

export const profileRepository = {
  /**
   * The access-control profile for a specific auth user. Returns null when no row
   * exists yet (the auth trigger normally creates one on sign-up). The user id is
   * passed in from the established session rather than re-fetched here: an internal
   * `auth.getUser()` raced session restoration on a cold load and cached a null
   * profile (fail-closed → no workspaces) that only a manual refresh cleared.
   */
  async current(userId: string): Promise<ProfileRow | null> {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (error) throw error;
    return data as ProfileRow | null;
  },

  /**
   * Every profile — for the admin Users page (BF-215). RLS ("admins read all
   * profiles", migration 0124) returns all rows only to an admin caller; a
   * non-admin gets just their own row, so this is safe to call anywhere but only
   * useful behind the admin gate.
   */
  async listAll(): Promise<ProfileRow[]> {
    const { data, error } = await supabase.from("profiles").select("*").order("email");
    if (error) throw error;
    return (data as ProfileRow[] | null) ?? [];
  },

  /**
   * Update a user's access fields (admin-only, RLS-gated by "admins update all
   * profiles", 0124). Never touches id/email (identity).
   */
  async updateAccess(
    id: string,
    patch: Partial<Pick<ProfileRow, "workspaces" | "can_write" | "is_admin" | "role">>
  ): Promise<ProfileRow> {
    const { data, error } = await supabase
      .from("profiles")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data as ProfileRow;
  },
};
